import React, { useEffect, useMemo, useState } from 'react';
import bemCn from 'bem-cn';
import loadable from '@loadable/component';
import {
    seoSubsAgilityFaqs,
    seoSubsBeachFaqs,
    seoSubsFencedFaqs,
    seoSubsFieldFaqs,
    seoSubsHikingFaqs,
    seoSubsIndoorFaqs,
    seoSubsPoolFaqs,
    seoSubsSmallFaqs,
    seoSubsWaterFaqs,
} from './faqs';
import { AUTH_TESTS } from '../listings/auth-test.const';
import { SIGN_UP_SRC } from '../../consts/profile.const';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { SUB_TYPE_PARK, SUB_TYPE_PARK_LISTINGS } from '../../consts/sub-type.consts';
import { RouteFormatter } from '../../routes';
import { COOKIE_PARAM_NAME } from '../../consts/cookies.consts';
import { RenderTopCities, RenderTopState } from '../../components/seo-listings/top-list';
import RenderAppDownloadCity from '../../components/seo-listings/app-download';
import SeoCitySingleHeader from '../../components/seo-listings/header';
import RenderOtherTypes from '../listings/render-other-types';
import SpotBreadcrumbs from '../../components/spot-breadcrumbs';
import RenderHowWorks from '../../components/seo-listings/how-works';
import RenderWhyRent from '../../components/seo-listings/why-rent';
import ToogleButton from '../../components/toogle-button';
import NearbyCities from '../../components/seo-listings/nearby-cities';
import SniffButton from '../../components/sniff-button';
import SniffAsSeen from '../../components/sniif-as-seen';
import SpotDetails from '../../components/seo-listings/spot-details';
import SeoReviews from '../../components/seo-listings/reviews';
import SharePage from '../../components/spot-share-button/share-page';
import FooterNew from '../../components/footer-new';
import SeoFaqs from '../../components/seo-listings/faqs';
import SvgAmenitiesKiddiePool from 'src/assets/svg/amenities/kiddie-pool.svg';
import SvgAmenitiesIndoor from 'src/assets/svg/amenities/indoor.svg';
import SvgAmenitiesTrail from 'src/assets/svg/amenities/trail.svg';
import SvgTopBadgeColor from 'src/assets/svg/SvgTopBadgeColor.svg';
import SvgChevronRight from 'src/assets/svg/SvgChevronRight.svg';
import SvgFilterNew from 'src/assets/svg/SvgFilterNew.svg';
import SvgSearch from 'src/assets/svg/SvgSearch.svg';
import SvgFenced from 'src/assets/svg/SvgFenced.svg';
import SvgDogPaw from 'src/assets/svg/SvgDogPaw.svg';
import SvgChTree from 'src/assets/svg/SvgChTree.svg';
import SvgRuler from 'src/assets/svg/SvgRuler.svg';
import SvgShare from 'src/assets/svg/SvgShare.svg';
import SvgDog from 'src/assets/svg/SvgDog.svg';
import SvgPin from 'src/assets/svg/SvgPin.svg';
import '../seo-city-single/index.scss';

const CookieService = require('../../services/cookie.service');
const useGetSpotPhotosPaginated = require('../../services/hooks/useGetSpotPhotosPaginated');

const SniffAppleMaps = loadable(() => import('../../components/sniff-apple-maps/city'));
const BlurSignupModal = loadable(() => import('../../components/modal-blur-signup'));

const b = bemCn('seo-city-single');

const LISTING_DEFAULT_MAP_ZM = 25000; // in meters

const toggleFiltersBtn = (subObj) => [
    {
        id: 'PUBLIC_PARK',
        text: 'Public park',
        svg: <SvgChTree />,
        isOn: true,
        auth: AUTH_TESTS.TOGGLE_PUBL_SPOT,
    },
    {
        id: 'PRIVATE_PARK',
        text: 'Private park',
        svg: <SvgDogPaw />,
        isOn: true,
        auth: AUTH_TESTS.TOGGLE_PRIV_SPOT,
    },
    {
        id: 'FULLY_FENCED',
        text: 'Fully fenced',
        svg: <SvgFenced />,
        auth: AUTH_TESTS.TOGGLE_FENCED,
        isOn: subObj.id == 'FENCED',
    },
    {
        id: 'WATER_PARKS',
        text: 'Water parks',
        svg: <SvgAmenitiesKiddiePool />,
        auth: AUTH_TESTS.TOGGLE_WATER,
        isOn: subObj.id == 'WATER',
    },
    {
        id: 'NO_DOGS',
        text: 'Fewer dogs seen/heard',
        svg: <SvgDog />,
        auth: AUTH_TESTS.TOGGLE_NODOGS,
    },
    {
        id: '10_MILES',
        text: '< 50 miles away',
        svg: <SvgPin />,
        auth: AUTH_TESTS.TOGGLE_IN10MILES,
        isOn: true,
    },
    {
        id: 'BIG_AREA',
        text: '0.5+ acres',
        svg: <SvgRuler />,
        auth: AUTH_TESTS.TOGGLE_MINHALFACR,
    },
    {
        id: 'TOP_SPOT',
        text: 'Top spots',
        svg: <SvgTopBadgeColor />,
        auth: AUTH_TESTS.TOGGLE_TOPSPOTS,
    },
    {
        id: 'HIKING',
        text: 'Hiking trails',
        svg: <SvgAmenitiesTrail />,
        auth: AUTH_TESTS.TOGGLE_HIKING,
        isOn: subObj.id == 'HIKING',
    },
    {
        id: 'INDOOR',
        text: 'Indoor play space',
        svg: <SvgAmenitiesIndoor />,
        auth: AUTH_TESTS.TOGGLE_INDOOR,
        isOn: subObj.id == 'INDOOR',
    },
    {
        id: 'FILTER',
        text: 'More...',
        svg: <SvgFilterNew />,
        auth: AUTH_TESTS.TOGGLE_FILTERS,
    },
];

const seoListBread = (city, title, cityUrl) => [
    {
        name: 'All dog parks',
        url: RouteFormatter.listings({}),
    },
    {
        name: city?.state || '',
        url: RouteFormatter.listings({ first: city?.stateUrl }),
    },
    {
        name: city?.name || '',
        url: cityUrl,
    },
    {
        name: title,
    },
];

const TriggerComp = () => (
    <ToogleButton className={b('filter-toggle_button')} activeCssClass="active" style={{ padding: '6px 16px' }} svg={<SvgShare className="me-0" />} />
);

const RenderFilters = ({ filtersList, getAuthTest, goToSearchPage, mobile }) => {
    const [shareModal, setShareModal] = useState(false);

    const toggleShareModal = () => setShareModal((s) => !s);

    return (
        <div className={b('filter-toggle').mix(mobile ? 'd-md-none' : '')}>
            <SharePage {...{ title: 'Share this page!', TriggerComp, shareModal, toggleShareModal }} />
            {filtersList.map((v) => (
                <ToogleButton
                    key={v.id}
                    className={b('filter-toggle_button')}
                    svg={v.svg}
                    name={v.text}
                    toogleOn={v.isOn}
                    onClick={v.auth ? () => getAuthTest(v.auth) : goToSearchPage}
                />
            ))}
        </div>
    );
};

const SeoAction = ({ city, exploreLink, subObj }) => (
    <div className="card-city action pt-4 pb-4">
        <h2 className="snif-m1 snif-l2-pc snif-semibold text-center mb-4 mt-md-3 mb-md-5">Rent safe and private dog parks</h2>
        <div className="text-center mb-md-3">
            <a href={exploreLink} rel="nofollow">
                <SniffButton size="lg" block="mobile">
                    Explore {subObj?.seoTitle.toLowerCase()} spots{subObj ? <br /> : ' '}in {city.name}, {city.stateShort.toUpperCase()}
                </SniffButton>
            </a>
        </div>
    </div>
);

const getMyFaqs = (city, slug) => {
    let myFaqs = {};

    switch (slug) {
        case 'dog-water-parks':
            myFaqs = seoSubsWaterFaqs(city);
            break;
        case 'fully-fenced-dog-parks':
            myFaqs = seoSubsFencedFaqs(city);
            break;
        case 'dog-agility-parks':
            myFaqs = seoSubsAgilityFaqs(city);
            break;
        case 'dog-fields':
            myFaqs = seoSubsFieldFaqs(city);
            break;
        case 'dog-hiking-trails':
            myFaqs = seoSubsHikingFaqs(city);
            break;
        case 'small-dog-parks':
            myFaqs = seoSubsSmallFaqs(city);
            break;
        case 'indoor-dog-parks':
            myFaqs = seoSubsIndoorFaqs(city);
            break;
        case 'dog-beaches':
            myFaqs = seoSubsBeachFaqs(city);
            break;
        case 'dog-swimming-pools':
            myFaqs = seoSubsPoolFaqs(city);
            break;
        default:
            break;
    }

    return myFaqs;
};

const CitySingleTest = ({ city, serverSSR }) => {
    const isMobile = useMobileScreen();
    const [showMap, setShowMap] = useState(false);
    const [isMapView, setisMapView] = useState(false);
    const [isBlurTest, setIsBlurTest] = useState(null);
    const [activeSpotId, setActiveSpotId] = useState(null);
    const { getSpotPhotosPaged } = serverSSR ? {} : useGetSpotPhotosPaginated.default();
    const cityText = `${city?.name}, ${city?.stateShort?.toUpperCase() || city?.state}`;
    const resultText = `${city.totalSpots} spot${city.totalSpots > 1 ? 's' : ''} found`;
    const pages = Math.ceil(city.totalSpots / 30);
    const showText = `Showing 1 - ${city.spots.length} of ${city.totalSpots > 100 ? '100+' : city.totalSpots} spots`;
    const subType = SUB_TYPE_PARK.find((s) => s.type == city.pageType);
    const subObj = SUB_TYPE_PARK_LISTINGS[subType?.id];
    const idx = city.url.indexOf(subType?.slug);
    const cityUrl = city.url.substring(0, idx - 1);
    const title = `Top public and private ${subObj?.title?.toLowerCase()} in ${city?.name}, ${city?.state}`;
    const subSeoDesc = subObj.seoDesc;
    const filterCounts = typeof subObj.filter === 'string' || !subObj.filter ? 2 : subObj.filter.length + 1;
    const filtersList = useMemo(() => toggleFiltersBtn(subObj), [subObj]);
    const faqs = useMemo(() => getMyFaqs(city, subType.slug), [city]);
    const listBread = useMemo(() => seoListBread(city, subType?.title || '', cityUrl), [city]);

    const exploreLink = useMemo(
        () =>
            encodeURI(
                RouteFormatter.listings(
                    { first: subObj.route },
                    {
                        query: {
                            loc: `${city.name}, ${city.stateShort.toUpperCase() || city.stateUrl}`,
                            lat: city.latitude || undefined,
                            lng: city.longitude || undefined,
                        },
                    }
                )
            ),
        [city]
    );

    const toggleMapView = () => setisMapView((m) => !m);

    const goToSearchPage = (page) => {
        window.location = RouteFormatter.listings({}, { query: { loc: cityText, page: page == 1 ? undefined : page } });
    };

    const getSpecificSpots = (item) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.location = item.link;
    };

    const getAuthTest = async (test) => {
        const re = `${window.location.pathname}#${test}`;
        CookieService.default.set(COOKIE_PARAM_NAME.REDIRECT, re);
        CookieService.default.set(COOKIE_PARAM_NAME.SIGN_UP_FROM, SIGN_UP_SRC.FILTERS_WEB, { expires: 1 });
        setIsBlurTest('v2');
        return true;
    };

    const filtersClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        getAuthTest(AUTH_TESTS.TOGGLE_FILTERS);
    };

    useEffect(() => {
        setShowMap(isMapView);
    }, [isMapView]);

    useEffect(() => {
        if (!isMobile) {
            setShowMap(true);
        }
    }, []);

    return (
        <>
            <SeoCitySingleHeader {...{ city, isMapView, getAuthTest, toggleMapView, filterCounts }} />
            <RenderFilters {...{ filtersList, getAuthTest, goToSearchPage }} mobile />
            <div className={b.mix('sub-city')}>
                <div className={b('main')}>
                    <div className={b('main-maps', { showMap })}>
                        <div className="d-none d-md-block">
                            <SpotBreadcrumbs {...{ listBread }} />
                            <div className="search-bar-closed" onClick={() => getAuthTest(AUTH_TESTS.TOGGLE_SEARCH)}>
                                <SvgSearch width={20} height={20} />
                                <div className="search-bar-closed_text-container">
                                    <div className="snif-s1">{cityText}</div>
                                    <div className="d-none d-md-block mx-1">/</div>
                                    <div className="snif-s2 snif-s1-pc">
                                        <span>Any date</span>•<span>Any time</span>
                                    </div>
                                </div>
                                <SniffButton className="filter-button" color="secondary" size="sm" onClick={filtersClick}>
                                    <SvgFilterNew width={20} height={20} />
                                    <div className="green-dot">{filterCounts}</div>
                                </SniffButton>
                            </div>
                        </div>
                        <div className={b('main-maps-content')}>
                            {showMap && (
                                <SniffAppleMaps
                                    center={{ latitude: parseFloat(city.latitude), longitude: parseFloat(city.longitude) }}
                                    spots={city.spots}
                                    cameraDistance={LISTING_DEFAULT_MAP_ZM}
                                    activeSpotId={activeSpotId}
                                    {...{ setMapRect: () => {}, setMapCenter: () => {}, onGetSpotsList: () => {} }}
                                />
                            )}
                        </div>
                    </div>
                    <div className={b('main-content')}>
                        <div className="d-md-none">
                            <SpotBreadcrumbs {...{ listBread }} />
                            <div className="px-2">
                                <h2 className="snif-m1 snif-semibold text-dark mt-2 mb-1">{title}</h2>
                                <p className="snif-s1 snif-regular text-dark mb-3">{subSeoDesc}</p>
                                <div className={b('main-content-mobile-header_result')}>{resultText}</div>
                            </div>
                        </div>
                        <div className="d-none d-md-block position-relative my-3 ms-1">
                            <div className={b('main-content-desktop-header')}>
                                <h1 className="snif-l1 snif-semibold text-dark">{title}</h1>
                                <div className={b('main-content-desktop-header_result')}>{resultText}</div>
                            </div>
                            <RenderFilters {...{ filtersList, getAuthTest, goToSearchPage }} />
                            <p className="snif-p snif-regular text-dark mt-3 mb-1">{subSeoDesc}</p>
                        </div>
                        <div className={b('listings')}>
                            {city.spots.map((spot, idx) => {
                                const first = idx === 0;
                                return (
                                    <SpotDetails
                                        key={spot.id}
                                        onMouseEnter={() => setActiveSpotId(spot.id)}
                                        onMouseLeave={() => setActiveSpotId(null)}
                                        {...{ spot, first, getSpotPhotosPaged }}
                                    />
                                );
                            })}
                        </div>
                        <div className={b('listings_footer')}>
                            <p className="d-none d-md-block snif-p snif-medium text-medium">{showText}</p>
                            <div className={b('pagination')}>
                                {[...Array(pages)].slice(0, 5).map((_, i) => (
                                    <span key={i} className={b('pagination_number', { active: i == 0 })} onClick={() => goToSearchPage(i + 1)}>
                                        {i + 1}
                                    </span>
                                ))}
                                {pages > 1 && (
                                    <span className={b('pagination_right')} onClick={() => goToSearchPage(2)}>
                                        <SvgChevronRight />
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="l-container">
                    <SniffAsSeen isMobileSSR={isMobile} useState={useState} />
                </div>
                <RenderWhyRent data={city} subObj={subObj} />
                <RenderOtherTypes
                    {...{
                        city,
                        subObj,
                        getSpecificSpots,
                        seoSubCityType: true,
                        isMobileSSR: isMobile,
                        SSR: true,
                        useState,
                        useEffect,
                    }}
                />
                <SeoReviews {...{ city, cityReviews: city.reviews, exploreLink, subObj }} />
                <RenderHowWorks {...{ isMobile, name: city?.name, subObj }} />
                <SeoFaqs {...{ city, faqs, subObj }} />
                <RenderAppDownloadCity />
                <NearbyCities {...{ city, subObj }} />
                <SeoAction {...{ city, exploreLink, subObj }} />
                <RenderTopCities SSR {...{ isMobile }} />
                <RenderTopState SSR {...{ isMobile }} />
            </div>
            <FooterNew {...{ isShowCities: false, isHome: true }} />
            <BlurSignupModal
                {...{
                    isBlurTest,
                    setIsBlurTest,
                    isMobile,
                    useStateSSR: useState,
                }}
            />
        </>
    );
};

export default CitySingleTest;
